import React, { useCallback, useEffect, useState } from 'react';
import {
  Newspaper, Plus, Save, X, Trash2, Eye, EyeOff, Upload, Loader2,
  ExternalLink, Search, Tag, BarChart3, Sparkles, Pin, PinOff, Bell, Mail, AlertTriangle,
} from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import ArticleBadge from '../ArticleBadge';
import PushArticleModal from './PushArticleModal';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  title: '',
  subtitle: '',
  photo_credit: '',
  published_at: '',
  author: 'Staff Writers',
  excerpt: '',
  slug: '',
  cover_image_url: '',
  body_html: '',
  tags: '',
  status: 'draft',
  send_email: false,
  column_slug: '',
});

const fmt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

/** UTC ISO -> the "YYYY-MM-DDTHH:mm" a datetime-local input wants, in Melbourne time. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Melbourne',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const ArticlesTab = ({ token, initialAction, onConsumeAction }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [query, setQuery] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [pushingArticleId, setPushingArticleId] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/articles`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setArticles(data.items || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  // Auto-open the New Article editor when triggered via PWA shortcut
  useEffect(() => {
    if (initialAction === 'new' && !showEditor) {
      setForm(emptyForm());
      setEditingId(null);
      setShowEditor(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (onConsumeAction) onConsumeAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction]);

  const regenExcerpts = async () => {
    if (!window.confirm(
      'Rewrite meta description excerpts on all articles that currently have weak ones (byline-only, too short, or empty)? ' +
      'This makes Google News listings more clickable. Fully manual excerpts you\'ve set yourself will be preserved.'
    )) return;
    setRegenerating(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/articles/regenerate-excerpts`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Regen failed.');
      }
      const data = await res.json();
      await loadArticles();
      alert(`Excerpts regenerated. Updated ${data.updated} · unchanged ${data.unchanged}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setRegenerating(false);
    }
  };

  const openEditor = (article) => {
    if (article) {
      // Fetch full article (list view omits body_html)
      fetch(`${API}/api/admin/articles/${article.id}`, { headers: authHeaders })
        .then((r) => r.ok ? r.json() : Promise.reject(new Error('Load failed')))
        .then((full) => {
          setForm({
            title: full.title || '',
            subtitle: full.subtitle || '',
            photo_credit: full.photo_credit || '',
            published_at: toLocalInput(full.published_at),
            author: full.author && full.author !== 'Outlaw Media' ? full.author : 'Staff Writers',
            excerpt: full.excerpt || '',
            slug: full.slug || '',
            cover_image_url: full.cover_image_url || '',
            body_html: full.body_html || '',
            tags: (full.tags || []).join(', '),
            status: full.status || 'draft',
            send_email: !!full.send_email,
            column_slug: full.column_slug || '',
          });
          setEditingId(full.id);
          setShowEditor(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        })
        .catch((e) => setError(e.message));
    } else {
      setForm(emptyForm());
      setEditingId(null);
      setShowEditor(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingId(null);
    setForm(emptyForm());
    setError('');
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      const presignRes = await fetch(`${API}/api/admin/articles/presign-upload`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ filename: file.name, content_type: file.type }),
      });
      if (!presignRes.ok) {
        const data = await presignRes.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not prepare upload.');
      }
      const { upload_url, public_url } = await presignRes.json();
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error('Upload failed'));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });
      setForm((f) => ({ ...f, cover_image_url: public_url }));
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body_html.trim()) {
      setError('Title and body are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        photo_credit: form.photo_credit.trim() || null,
        published_at: form.published_at || null,
        author: form.author || 'Staff Writers',
        excerpt: form.excerpt.trim() || null,
        slug: form.slug.trim() || null,
        cover_image_url: form.cover_image_url || null,
        body_html: form.body_html,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        status: form.status,
        send_email: !!form.send_email,
      };
      const url = editingId
        ? `${API}/api/admin/articles/${editingId}`
        : `${API}/api/admin/articles`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      await loadArticles();
      closeEditor();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    await fetch(`${API}/api/admin/articles/${article.id}`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ status: newStatus }),
    });
    await loadArticles();
  };

  const togglePin = async (article) => {
    if (article.pinned) {
      const res = await fetch(`${API}/api/admin/articles/${article.id}/pin`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        window.alert(d.detail || 'Unpin failed');
      }
    } else {
      const currentlyPinned = articles.find((a) => a.pinned && a.id !== article.id);
      if (currentlyPinned && !window.confirm(
        `"${currentlyPinned.title}" is currently pinned.\n\nOnly one article can be pinned at a time.\nReplace it with "${article.title}"?`
      )) return;
      const res = await fetch(`${API}/api/admin/articles/${article.id}/pin`, {
        method: 'POST', headers: authHeaders,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        window.alert(d.detail || 'Pin failed. Article must be published first.');
      }
    }
    await loadArticles();
  };

  const remove = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    await fetch(`${API}/api/admin/articles/${article.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    await loadArticles();
  };

  const sendPush = (article) => {
    setPushingArticleId(article.id);
  };

  const filtered = query.trim()
    ? articles.filter((a) => (a.title || '').toLowerCase().includes(query.toLowerCase()))
    : articles;

  return (
    <div className="space-y-5" data-testid="articles-tab">
      {pushingArticleId && (
        <PushArticleModal
          articleId={pushingArticleId}
          onClose={() => setPushingArticleId(null)}
          onSent={() => loadArticles()}
        />
      )}
      {/* Editor panel */}
      {showEditor && (
        <form onSubmit={submit} className="rounded-lg border border-red-900/30 bg-zinc-900/60 p-5 space-y-4" data-testid="article-form">
          <div className="flex items-center gap-2">
            {editingId ? <Save className="h-5 w-5 text-red-500" /> : <Plus className="h-5 w-5 text-red-500" />}
            <h3 className="text-xl font-black uppercase tracking-wider">
              {editingId ? 'Edit Article' : 'New Article'}
            </h3>
            <button type="button" onClick={closeEditor}
              className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white">
              <X className="h-3.5 w-3.5" /> Close
            </button>
          </div>

          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Title *</label>
            <input
              type="text" required maxLength={280}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="article-title"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-lg text-white outline-none font-black"
            />
          </div>

          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Subtitle</label>
            <input
              type="text" maxLength={400}
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              data-testid="article-subtitle"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                Byline (who wrote it)
              </label>
              <select
                value={form.author || 'Staff Writers'}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                data-testid="article-author-select"
                className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
              >
                <option value="Staff Writers">Staff Writers</option>
                <option value="Ryan Naumenko">Ryan Naumenko</option>
                <option value="The Roadman">The Roadman</option>
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                Shows on the story and in Google&apos;s byline data.
              </p>
            </div>
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                Hero photo credit
              </label>
              <input
                type="text" maxLength={120}
                value={form.photo_credit}
                onChange={(e) => setForm({ ...form, photo_credit: e.target.value })}
                placeholder="Photo: Outlaw Media"
                data-testid="article-photo-credit"
                className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Stamped on the hero image. Leave blank for &quot;Photo: Outlaw Media&quot;.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">
                Publish date (Melbourne)
              </label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                data-testid="article-published-at"
                className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Set once when it first runs and never moved by an unpublish. Change it only to fix a
                date.
              </p>
            </div>
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Slug (auto if blank)</label>
              <input
                type="text" maxLength={200}
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                data-testid="article-slug"
                placeholder="melbourne-underworld-latest"
                className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Tags (comma-separated)</label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                data-testid="article-tags"
                placeholder="gangland, melbourne, exclusive"
                className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
              />
              {/* One-tap badge tags - these drive the kicker on the story. */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[
                  { tag: 'exclusive', label: 'Exclusive', cls: 'bg-yellow-400 text-black' },
                  { tag: 'breaking', label: 'Breaking', cls: 'bg-red-600 text-white' },
                  { tag: 'catch up', label: 'Catch Up', cls: 'bg-purple-600 text-white' },
                  { tag: 'opinion', label: 'Opinion', cls: 'bg-violet-500 text-white' },
                ].map(({ tag, label, cls }) => {
                  const current = form.tags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
                  const on = current.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      data-testid={`article-badge-tag-${tag.replace(/\s+/g, '-')}`}
                      onClick={() => {
                        const next = on
                          ? current.filter((t) => t !== tag)
                          : [...current, tag];
                        setForm({ ...form, tags: next.join(', ') });
                      }}
                      className={`px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em] transition-opacity ${cls} ${on ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Cover image</label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <input
                type="url" maxLength={1000}
                value={form.cover_image_url}
                onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                placeholder="Paste URL or upload below"
                data-testid="article-cover-url"
                className="flex-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
              />
              <label className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-[11px] font-black uppercase tracking-widest cursor-pointer whitespace-nowrap">
                <Upload className="h-3.5 w-3.5" />
                {uploading ? `${uploadProgress}%` : 'Upload'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  disabled={uploading}
                  onChange={(e) => uploadCover(e.target.files?.[0])}
                  data-testid="article-cover-upload"
                  className="hidden"
                />
              </label>
            </div>
            {form.cover_image_url && (
              <div className="mt-2 inline-block rounded border border-zinc-800 overflow-hidden bg-zinc-950 p-1">
                <img src={form.cover_image_url} alt="cover preview" className="max-h-32 max-w-full" />
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Excerpt (auto-generated if blank)</label>
            <textarea
              maxLength={600}
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              data-testid="article-excerpt"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none resize-y"
            />
          </div>

          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1 block">Body *</label>
            <RichTextEditor
              value={form.body_html}
              onChange={(html) => setForm((f) => ({ ...f, body_html: html }))}
              token={token}
              placeholder="Write your story… use the toolbar to format, insert images and links."
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.status === 'published'}
                onChange={(e) => setForm({ ...form, status: e.target.checked ? 'published' : 'draft' })}
                data-testid="article-status-toggle"
                className="h-4 w-4 accent-red-600"
              />
              <span className="text-xs font-black uppercase tracking-widest text-gray-300">
                Published (live on the site)
              </span>
            </label>

            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.send_email}
                onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                data-testid="article-send-email-toggle"
                className="h-4 w-4 accent-amber-500"
              />
              <span className="text-xs font-black uppercase tracking-widest text-gray-300">
                Email subscribers on publish
              </span>
            </label>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Publish to</span>
              <select
                value={form.column_slug || ''}
                onChange={(e) => setForm({ ...form, column_slug: e.target.value })}
                data-testid="article-column-select"
                className="bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-2.5 py-1.5 text-sm text-white outline-none"
              >
                <option value="">Main news feed</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="bg-red-950/40 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm" data-testid="article-form-error">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="article-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-[11px] font-black uppercase tracking-widest"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {editingId ? 'Save Changes' : 'Create Article'}
            </button>
            <button
              type="button"
              onClick={closeEditor}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[11px] font-black uppercase tracking-widest"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Header / actions */}
      {!showEditor && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Newspaper className="h-5 w-5 text-red-500" />
            <h3 className="text-xl font-black uppercase tracking-wider">Articles</h3>
            <span className="text-xs text-gray-500">
              {loading ? 'Loading…' : `${articles.length} total`}
            </span>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search title…"
                  data-testid="articles-search"
                  className="bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-1.5 pl-8 text-sm text-white outline-none w-56"
                />
              </div>
              <button
                type="button"
                onClick={() => openEditor(null)}
                data-testid="article-new-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-widest"
              >
                <Plus className="h-3.5 w-3.5" /> New Article
              </button>
              <button
                type="button"
                onClick={regenExcerpts}
                disabled={regenerating}
                data-testid="article-regen-excerpts-btn"
                title="Rewrites weak meta descriptions (bylines, short blurbs) for better Google News CTR"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white text-[11px] font-black uppercase tracking-widest"
              >
                {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Regen Excerpts
              </button>
            </div>
          </div>

          {!loading && filtered.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-10">
              {query ? `No matches for "${query}".` : 'No articles yet. Create the first one.'}
            </p>
          )}

          <div className="space-y-2">
            {filtered.map((a) => (
              <div
                key={a.id}
                className={`rounded-lg border p-3 flex items-start gap-3 ${
                  a.status === 'published'
                    ? 'border-emerald-500/30 bg-emerald-950/10'
                    : 'border-zinc-800 bg-zinc-950/50'
                }`}
                data-testid={`article-row-${a.id}`}
              >
                {a.cover_image_url ? (
                  <img src={a.cover_image_url} alt="" className="w-20 h-16 object-cover rounded shrink-0 bg-zinc-950" />
                ) : (
                  <div className="w-20 h-16 rounded shrink-0 bg-zinc-950 border border-zinc-800 flex items-center justify-center">
                    <Newspaper className="h-5 w-5 text-gray-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="text-sm font-black text-white truncate">{a.title}</h4>
                    <ArticleBadge article={a} size="sm" testId={`article-row-badge-${a.id}`} />
                    {a.source === 'imported' && (
                      <span className="text-[9px] font-black tracking-widest uppercase text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 px-1.5 py-0.5 rounded">
                        imported
                      </span>
                    )}
                    {a.status === 'published' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                        <Eye className="h-2.5 w-2.5" /> Live
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-zinc-800 text-gray-400 border border-zinc-700">
                        <EyeOff className="h-2.5 w-2.5" /> Draft
                      </span>
                    )}
                    {a.pinned && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-yellow-500/15 text-yellow-300 border border-yellow-500/50"
                        data-testid={`article-pinned-badge-${a.id}`}
                      >
                        <Pin className="h-2.5 w-2.5" /> Pinned
                      </span>
                    )}
                  </div>
                  {a.subtitle && <p className="text-xs text-gray-400 truncate">{a.subtitle}</p>}
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500 mt-1 flex-wrap">
                    <span>/stories/{a.slug}</span>
                    <span>· {fmt(a.published_at || a.created_at)}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-400 font-black" data-testid={`article-views-${a.id}`}>
                      <BarChart3 className="h-2.5 w-2.5" /> {(a.views || 0).toLocaleString('en-AU')} views
                    </span>
                    {a.tags && a.tags.length > 0 && (
                      <span className="flex items-center gap-1"><Tag className="h-2.5 w-2.5" /> {a.tags.join(', ')}</span>
                    )}
                    {typeof a.email_sent_count === 'number' && (
                      <span
                        data-testid={`article-email-stats-${a.id}`}
                        className="inline-flex items-center gap-1 text-blue-300 font-black"
                        title={`Broadcast email - sent ${a.email_sent_count}${a.email_failed_count ? ` · failed ${a.email_failed_count}` : ''}${a.last_emailed_at ? ` · ${fmt(a.last_emailed_at)}` : ''}`}
                      >
                        <Mail className="h-2.5 w-2.5" /> {a.email_sent_count} sent
                      </span>
                    )}
                    {(a.email_bounce_count > 0 || a.email_complaint_count > 0) && (
                      <span
                        data-testid={`article-email-bounces-${a.id}`}
                        className="inline-flex items-center gap-1 text-red-300 font-black bg-red-950/50 border border-red-500/40 px-1.5 py-0.5 rounded"
                        title="Bounces/complaints attributed to this broadcast"
                      >
                        <AlertTriangle className="h-2.5 w-2.5" />
                        {a.email_bounce_count > 0 && `${a.email_bounce_count} bounce${a.email_bounce_count === 1 ? '' : 's'}`}
                        {a.email_bounce_count > 0 && a.email_complaint_count > 0 && ' · '}
                        {a.email_complaint_count > 0 && `${a.email_complaint_count} complaint${a.email_complaint_count === 1 ? '' : 's'}`}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <button type="button" onClick={() => openEditor(a)}
                      className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest">
                      Edit
                    </button>
                    <button type="button" onClick={() => togglePublish(a)}
                      className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest">
                      {a.status === 'published' ? 'Unpublish' : 'Publish'}
                    </button>
                    {a.status === 'published' && (
                      <button
                        type="button"
                        onClick={() => togglePin(a)}
                        data-testid={`article-pin-btn-${a.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${
                          a.pinned
                            ? 'bg-yellow-500/20 border border-yellow-500/60 text-yellow-200 hover:bg-yellow-500/30'
                            : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                        }`}
                        title={a.pinned ? 'Unpin from top of /stories' : 'Pin to top of /stories (replaces any current pin)'}
                      >
                        {a.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                        {a.pinned ? 'Unpin' : 'Pin'}
                      </button>
                    )}
                    {a.status === 'published' && (
                      <button
                        type="button"
                        onClick={() => sendPush(a)}
                        data-testid={`article-push-btn-${a.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 hover:bg-red-900/50 border border-red-900/40 hover:border-red-500/70 text-red-300 hover:text-white text-[10px] font-black uppercase tracking-widest"
                        title={a.last_pushed_at
                          ? `Last pushed as "${a.last_push_label || 'UPDATED'}" · ${fmt(a.last_pushed_at)}`
                          : 'Broadcast a push notification for this article'}
                      >
                        <Bell className="h-3 w-3" />
                        {a.last_pushed_at ? 'Push again' : 'Send push'}
                      </button>
                    )}
                    <a href={`/stories/${a.slug}`} target="_blank" rel="noopener noreferrer"
                      className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                      <ExternalLink className="h-2.5 w-2.5" /> View
                    </a>
                    <button type="button" onClick={() => remove(a)}
                      className="px-2 py-1 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300 text-[10px] font-black uppercase tracking-widest">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesTab;
